/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { type NavLinkProps, useMantineTheme } from '@mantine/core';
import { RiTerminalBoxFill, RiBox3Fill, RiCloudFill } from '@remixicon/react';

import i18n from '@fai/friendli-suite/features/i18n';
import { useContainerTeamPlan } from '@fai/friendli-suite/features/products/container/plan/use-container-team-plan';
import { useDedicatedEndpointsTeamPlan } from '@fai/friendli-suite/features/products/dedicated-endpoints/plan/use-dedicated-endpoints-team-plan';
import PlanTag from '@fai/friendli-suite/features/products/plan-tag';
import { getContainerTagVariants } from '@fai/friendli-suite/features/products/plan-tag/tag-variants/container-tag-variants';
import { getDedicatedEndpointsTagVariants } from '@fai/friendli-suite/features/products/plan-tag/tag-variants/dedicated-endpoints-tag-variants';
import { getServerlessEndpointsTagVariants } from '@fai/friendli-suite/features/products/plan-tag/tag-variants/serverless-endpoints-tag-variants';
import { useServerlessEndpointsTeamPlan } from '@fai/friendli-suite/features/products/serverless-endpoints/plan/use-serverless-endpoints-team-plan';
import Paths from '@fai/friendli-suite/features/routes/paths';

type NavLinkItemProps = NavLinkProps & {
  value: string;
  href: string;
};

export const useProductList = ({ teamId }: { teamId: string }): NavLinkItemProps[] => {
  const theme = useMantineTheme();

  const { plan: serverlessEndpointsPlan } = useServerlessEndpointsTeamPlan(teamId);
  const { plan: containerPlan } = useContainerTeamPlan(teamId);
  const { plan: dedicatedEndpointsPlan } = useDedicatedEndpointsTeamPlan(teamId);

  const serverlessEndpointsTagVariants = getServerlessEndpointsTagVariants(theme, {
    isUsedInHeader: true,
  });
  const containerTagVariants = getContainerTagVariants(theme, {
    isUsedInHeader: true,
  });
  const dedicatedEndpointsTagVariants = getDedicatedEndpointsTagVariants(theme);

  return [
    {
      value: 'serverless-endpoints',
      label: i18n.t('layout.friendli_serverless_endpoints'),
      leftSection: <RiTerminalBoxFill size={16} />,
      href: Paths.team(teamId).serverlessEndpoints.default,
      rightSection: <PlanTag {...serverlessEndpointsTagVariants[serverlessEndpointsPlan]} />,
    },
    {
      value: 'dedicated-endpoints',
      label: i18n.t('layout.friendli_dedicated_endpoints'),
      leftSection: <RiCloudFill size={16} />,
      href: Paths.team(teamId).dedicatedEndpoints.default,
      rightSection: <PlanTag {...dedicatedEndpointsTagVariants[dedicatedEndpointsPlan]} />,
    },
    {
      value: 'container',
      label: i18n.t('layout.friendli_container'),
      leftSection: <RiBox3Fill size={16} />,
      href: Paths.team(teamId).container.default,
      rightSection: <PlanTag {...containerTagVariants[containerPlan]} />,
    },
  ];
};
